import { createSelector, createSlice } from '@reduxjs/toolkit';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';

import { CATEGORIES, SUBCATEGORIES } from '../common/categories';
import {
    ACCOUNT_MATCHING_FIELDS,
    ADFS_APP_FIELDS,
    ADMIN_SECURITY_ROLES_FIELDS,
    ADVANCED_CONFIG_FIELDS,
    APPLICATION_SETTINGS_FIELDS,
    AVAILABILITY_ZONE_FIELDS,
    AZURE_CONNECTOR_FIELDS,
    AZURE_CONNECTORS_FIELDS,
    COMPANY_INFO_FIELDS,
    CONFIG_SPECIFIC_NODE_FIELDS,
    CUSTOM_INSIGHTS_FIELDS,
    DESKTOP_MESSAGING_SERVICES_FIELDS,
    DESKTOP_NOTIFICATION_FIELDS,
    DUO_MFA_TOKEN_FIELDS,
    ELASTIC_CONFIG_FIELDS,
    FAIL_CLOSE_FIELDS,
    FIDO2_FIELDS,
    HYPR_MFA_TOKEN_FIELDS,
    LICENSE_FIELDS,
    MAINTENANCE_FIELDS,
    MESSAGING_SERVICE_FIELDS,
    MICROSOFT_MFA_TOKEN_FIELDS,
    MOBILE_MESSAGING_SERVICES_FIELDS,
    NAMING_CONVENTIONS_FIELDS,
    NEW_AZURE_CONNECTOR_FIELDS,
    NODE_FIELDS,
    OKTA_CONNECTOR_FIELDS,
    OKTA_CONNECTORS_FIELDS,
    OKTA_MFA_TOKEN_FIELDS,
    OTP_NOTIFICATION_FIELDS,
    PING_MFA_TOKEN_FIELDS,
    PINGFED_APP_FIELDS,
    PROTECTED_DOMAIN_FIELDS,
    RADIUS_FIELDS,
    RSA_MFA_TOKEN_FIELDS,
    SECURITY_MAIN_FIELDS,
    SELECTIVE_FWD_FIELDS,
    SF_SUPPORT_FIELDS,
    SILVERFORT_API_FIELDS,
    SMTP_SERVER_FIELDS,
    SSO_FIELDS,
    ENROLLMENT_SETTINGS_FIELDS
} from '../common/fields';

const buildCategory = (category, categoryTerms = []) => {
    const cat = {
        id: category.ID,
        name: category.NAME,
        terms: [],
        enabled: true,
        icon: category.ICON
    };
    for (const term of categoryTerms) {
        if (term.NAME) {
            cat.terms.push(term.NAME);
        }
        if (term.TAGS) {
            cat.terms.push(...term.TAGS);
        }
    }

    return cat;
};

const updateSubCategoriesBySystemMode = (systemMode, state, isCloudOnly) => {
    if (systemMode === SYSTEM_MODES.SAAS_SUPPORT || isCloudOnly) {
        const advancedCategory = state.categories[CATEGORIES.ADVANCED.ID];
        advancedCategory.terms = [
            ...advancedCategory.terms.filter((term) => term !== SUBCATEGORIES.ADVANCED_CONFIG.NAME),
            SUBCATEGORIES.ADVANCED_CONFIG.SAAS_SUPPORT_NAME
        ];
    }
};

const updateCategoriesByPermissions = (permissions, state) => {
    if (permissions.cloudIntegrations < ROLES.VIEWER.value) {
        state.categories[CATEGORIES.CLOUD_INTEGRATIONS.ID].enabled = false;
    }
};

export const slice = createSlice({
    name: 'settings',
    initialState: {
        categories: {
            [CATEGORIES.GENERAL.ID]: buildCategory(CATEGORIES.GENERAL, [
                SUBCATEGORIES.COMPANY_INFO,
                SUBCATEGORIES.RADIUS_CLIENTS,
                SUBCATEGORIES.SMTP_SERVER,
                SUBCATEGORIES.FAIL_CLOSE,
                SUBCATEGORIES.SELECTIVE_FORWARDING,
                ...Object.values(COMPANY_INFO_FIELDS),
                ...Object.values(SMTP_SERVER_FIELDS),
                ...Object.values(RADIUS_FIELDS),
                ...Object.values(FAIL_CLOSE_FIELDS),
                ...Object.values(SELECTIVE_FWD_FIELDS)
            ]),
            [CATEGORIES.SECURITY.ID]: buildCategory(CATEGORIES.SECURITY, [
                SUBCATEGORIES.SECURITY_MAIN,
                SUBCATEGORIES.ADMIN_SECURITY_ROLES,
                SUBCATEGORIES.SSO_CONFIG,
                SUBCATEGORIES.SF_SUPPORT,
                ...Object.values(SF_SUPPORT_FIELDS),
                ...Object.values(SECURITY_MAIN_FIELDS),
                ...Object.values(SSO_FIELDS),
                ...Object.values(ADMIN_SECURITY_ROLES_FIELDS)
            ]),
            [CATEGORIES.PROTECTED_DOMAINS.ID]: buildCategory(CATEGORIES.PROTECTED_DOMAINS, [
                ...Object.values(PROTECTED_DOMAIN_FIELDS)
            ]),
            [CATEGORIES.SILVERFORT_NODES.ID]: buildCategory(CATEGORIES.SILVERFORT_NODES, [
                ...Object.values(NODE_FIELDS)
            ]),
            [CATEGORIES.AVAILABILITY_ZONES.ID]: buildCategory(CATEGORIES.AVAILABILITY_ZONES, [
                ...Object.values(AVAILABILITY_ZONE_FIELDS)
            ]),
            [CATEGORIES.MFA_TOKENS.ID]: buildCategory(CATEGORIES.MFA_TOKENS, [
                SUBCATEGORIES.DUO_MFA_TOKEN,
                SUBCATEGORIES.RSA_MFA_TOKEN,
                SUBCATEGORIES.OKTA_MFA_TOKEN,
                SUBCATEGORIES.PING_MFA_TOKEN,
                SUBCATEGORIES.MICROSOFT_MFA_TOKEN,
                SUBCATEGORIES.FIDO2_MFA_TOKEN,
                ...Object.values(DUO_MFA_TOKEN_FIELDS),
                ...Object.values(RSA_MFA_TOKEN_FIELDS),
                ...Object.values(OKTA_MFA_TOKEN_FIELDS),
                ...Object.values(PING_MFA_TOKEN_FIELDS),
                ...Object.values(MICROSOFT_MFA_TOKEN_FIELDS),
                ...Object.values(HYPR_MFA_TOKEN_FIELDS),
                ...Object.values(FIDO2_FIELDS)
            ]),
            [CATEGORIES.USER_MANAGEMENT.ID]: buildCategory(CATEGORIES.USER_MANAGEMENT, [
                SUBCATEGORIES.ACCOUNT_MATCHING,
                SUBCATEGORIES.ENROLLMENT_SETTINGS,
                ...Object.values(ACCOUNT_MATCHING_FIELDS),
                ...Object.values(ENROLLMENT_SETTINGS_FIELDS)
            ]),
            [CATEGORIES.DESKTOP_APPLICATION.ID]: buildCategory(CATEGORIES.DESKTOP_APPLICATION, [
                SUBCATEGORIES.DESKTOP_MESSAGING_SERVICES,
                SUBCATEGORIES.DESKTOP_NOTIFICATION,
                SUBCATEGORIES.OTP_NOTIFICATION,
                ...Object.values(APPLICATION_SETTINGS_FIELDS),
                ...Object.values(DESKTOP_MESSAGING_SERVICES_FIELDS),
                ...Object.values(MESSAGING_SERVICE_FIELDS),
                ...Object.values(DESKTOP_NOTIFICATION_FIELDS),
                ...Object.values(OTP_NOTIFICATION_FIELDS)
            ]),
            [CATEGORIES.INTEGRATIONS.ID]: buildCategory(CATEGORIES.INTEGRATIONS, [
                SUBCATEGORIES.ADFS_FEDERATED_APPS,
                SUBCATEGORIES.PING_FEDERATED_APPS,
                SUBCATEGORIES.AZURE_CONNECTORS,
                ...Object.values(ADFS_APP_FIELDS),
                ...Object.values(PINGFED_APP_FIELDS),
                ...Object.values(AZURE_CONNECTORS_FIELDS),
                ...Object.values(AZURE_CONNECTOR_FIELDS),
                ...Object.values(NEW_AZURE_CONNECTOR_FIELDS),
                ...Object.values(OKTA_CONNECTORS_FIELDS),
                ...Object.values(OKTA_CONNECTOR_FIELDS)
            ]),
            [CATEGORIES.CLOUD_INTEGRATIONS.ID]: buildCategory(CATEGORIES.CLOUD_INTEGRATIONS, []),
            [CATEGORIES.PRIVILEGED_ACCESS.ID]: buildCategory(CATEGORIES.PRIVILEGED_ACCESS, [
                SUBCATEGORIES.RESOURCES,
                SUBCATEGORIES.JIT
            ]),
            [CATEGORIES.NON_HUMAN_IDENTITIES.ID]: buildCategory(CATEGORIES.NON_HUMAN_IDENTITIES, [
                SUBCATEGORIES.NHI_ON_PREM
            ]),
            [CATEGORIES.SILVERFORT_API.ID]: buildCategory(CATEGORIES.SILVERFORT_API, [
                ...Object.values(SILVERFORT_API_FIELDS)
            ]),
            [CATEGORIES.CUSTOM_INSIGHTS.ID]: buildCategory(CATEGORIES.CUSTOM_INSIGHTS, [
                SUBCATEGORIES.PRIVILEGED_ACCOUNTS,
                SUBCATEGORIES.SERVICE_ACCOUNTS,
                ...Object.values(CUSTOM_INSIGHTS_FIELDS),
                ...Object.values(NAMING_CONVENTIONS_FIELDS)
            ]),
            [CATEGORIES.ADVANCED.ID]: buildCategory(CATEGORIES.ADVANCED, [
                SUBCATEGORIES.MAINTENANCE,
                SUBCATEGORIES.ADVANCED_CONFIG,
                SUBCATEGORIES.ELASTIC_CONFIG,
                ...Object.values(MAINTENANCE_FIELDS),
                ...Object.values(ADVANCED_CONFIG_FIELDS),
                ...Object.values(CONFIG_SPECIFIC_NODE_FIELDS),
                ...Object.values(ELASTIC_CONFIG_FIELDS)
            ]),
            [CATEGORIES.LICENSING.ID]: buildCategory(CATEGORIES.LICENSING, [
                SUBCATEGORIES.LICENSE,
                SUBCATEGORIES.MOBILE_MESSAGING_SERVICES,
                ...Object.values(LICENSE_FIELDS),
                ...Object.values(MOBILE_MESSAGING_SERVICES_FIELDS)
            ]),
            [CATEGORIES.ALERTS_MANAGEMENT.ID]: buildCategory(CATEGORIES.ALERTS_MANAGEMENT, [
                SUBCATEGORIES.WEBHOOK,
                SUBCATEGORIES.SYSLOG_SERVER
            ])
        },
        searchQuery: '',
        settings: {},
        schemas: {},
        towerSchema: {},
        systemState: false,
        isSaving: false,
        isDirty: false
    },
    reducers: {
        initSettings: (state, action) => {
            const settings = action.payload.Settings;
            state.settings = settings;
            state.schemas = action.payload.schemas;
            state.towerSchema = action.payload.towerSchema;
            state.systemState = action.payload.SystemState;
            const userPermissions = action.payload.user.permissions;

            const { NodesConfigurations, DomainsConfigurations, UserCustomInsights, systemMode } = settings;
            if (NodesConfigurations) {
                for (const nodeId in NodesConfigurations) {
                    state.categories[CATEGORIES.SILVERFORT_NODES.ID].terms.push(NodesConfigurations[nodeId].Name);
                }
            }
            if (DomainsConfigurations) {
                for (const domainId in DomainsConfigurations) {
                    state.categories[CATEGORIES.PROTECTED_DOMAINS.ID].terms.push(
                        DomainsConfigurations[domainId].DomainName
                    );
                }
            }
            if (UserCustomInsights) {
                for (const customInsight of UserCustomInsights) {
                    state.categories[CATEGORIES.CUSTOM_INSIGHTS.ID].terms.push(customInsight.name);
                }
            }

            updateSubCategoriesBySystemMode(systemMode, state, action.payload.isCloudOnly);
            updateCategoriesByPermissions(userPermissions, state);
        },
        saveSettings: (state, action) => {
            for (const settingKey of Object.keys(action.payload)) {
                state.settings[settingKey] = action.payload[settingKey];
            }
        },
        search: (state, action) => {
            state.searchQuery = action.payload;
            if (!action.payload) {
                for (const category in state.categories) {
                    state.categories[category].enabled = true;
                }
            } else {
                for (const category in state.categories) {
                    state.categories[category].enabled = false;
                    if (state.categories[category].name.toLowerCase().includes(action.payload.toLowerCase())) {
                        state.categories[category].enabled = true;
                        continue;
                    }
                    for (const term of state.categories[category].terms) {
                        if (term.toLowerCase().includes(action.payload.toLowerCase())) {
                            state.categories[category].enabled = true;
                            break;
                        }
                    }
                }
            }
        },
        deleteFromSettingsById: (state, action) => {
            const { settingKey, deletedId } = action.payload;
            if (state.settings[settingKey]) {
                delete state.settings[settingKey][deletedId];
            }
        },

        deleteDomainById: (state, action) => {
            const { deletedId } = action.payload;
            delete state.settings.DomainsConfigurations[deletedId];
            const newNodesConfiguration = {};

            for (const nodeId in state.settings.NodesConfigurations) {
                const nodeConfiguration = { ...state.settings.NodesConfigurations[nodeId] };
                if (deletedId in nodeConfiguration.domainsConfiguration) {
                    delete nodeConfiguration.domainsConfiguration[deletedId];
                }
                newNodesConfiguration[nodeId] = nodeConfiguration;
            }
            state.settings.NodesConfigurations = newNodesConfiguration;
        },

        deleteNodeById: (state, action) => {
            const { deletedId } = action.payload;
            delete state.settings.NodesConfigurations[deletedId];
            const newDomainsConfigurations = {};

            for (const domainId in state.settings.DomainsConfigurations) {
                const domainConfigurations = { ...state.settings.DomainsConfigurations[domainId] };
                const nodeIndex = (domainConfigurations.ADSyncDomainNodes || []).findIndex(
                    (nodeId) => nodeId === deletedId
                );
                if (nodeIndex >= 0) {
                    domainConfigurations.ADSyncDomainNodes.splice(nodeIndex, 1);
                }
                newDomainsConfigurations[domainId] = domainConfigurations;
            }
            state.settings.DomainsConfigurations = newDomainsConfigurations;

            for (const az in state.settings.azConfig) {
                state.settings.azConfig[az].walls = state.settings.azConfig[az].walls.filter(
                    (wall) => wall !== deletedId
                );
            }
        },

        deleteAzById: (state, action) => {
            const { deletedId, newUnassignedDcs, newUnassignedWalls } = action.payload;
            delete state.settings.azConfig[deletedId];
            state.settings.AzConfigExtraData.unassigned.dcs = [
                ...state.settings.AzConfigExtraData.unassigned.dcs,
                ...newUnassignedDcs
            ];
            state.settings.AzConfigExtraData.unassigned.walls = [
                ...state.settings.AzConfigExtraData.unassigned.walls,
                ...newUnassignedWalls
            ];
        },

        deleteFromSettingsByIndex: (state, action) => {
            const { settingKey, deletedIndex } = action.payload;
            if (Array.isArray(state.settings[settingKey]) && state.settings[settingKey].length > deletedIndex) {
                state.settings[settingKey].splice(deletedIndex, 1);
            }
        },
        hideWizardOnFirstTime: (state) => {
            state.settings.FirstTimeOnSettings = false;
        }
    }
});

export const {
    initSettings,
    saveSettings,
    search,
    deleteFromSettingsById,
    deleteFromSettingsByIndex,
    deleteDomainById,
    deleteNodeById,
    hideWizardOnFirstTime,
    deleteAzById
} = slice.actions;

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched
// export const incrementAsync = (amount) => (dispatch) => {
//     setTimeout(() => {
//         dispatch(incrementByAmount(amount));
//     }, 1000);
// };

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.counter.value)`
export const selectCategories = createSelector([(state) => state.settings.categories], (categories) =>
    Object.values(categories)
);

export default slice.reducer;
